import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { marked } from 'marked';
import { apiGet, apiPost } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { ForeverFreeDetailResponse, TermsAndConditionsResponse } from '../types/api';
import AlertMessage from '../components/AlertMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import './AcceptForeverFreePage.css';

export default function AcceptForeverFreePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [invite, setInvite] = useState<ForeverFreeDetailResponse | null>(null);
  const [terms, setTerms] = useState<TermsAndConditionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsScrolled, setTermsScrolled] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current || !token) return;
    fetched.current = true;

    (async () => {
      try {
        const [inv, tc] = await Promise.all([
          apiGet<ForeverFreeDetailResponse>(`/api/accept-free-vault/${token}`),
          apiGet<TermsAndConditionsResponse>('/api/terms/current'),
        ]);
        setInvite(inv);
        setTerms(tc);
      } catch (err: any) {
        setError(err.message || 'Unable to load invitation.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  function savePending() {
    if (!token || !terms) return;
    localStorage.setItem(
      'pendingForeverFreeAccept',
      JSON.stringify({ token, acceptedTermsId: terms.id, email: invite?.email })
    );
  }

  function handleCreateAccount() {
    savePending();
    navigate('/register');
  }

  function handleSignIn() {
    savePending();
    navigate('/login');
  }

  async function handleAccept() {
    if (!token || !terms || !termsAccepted) return;
    setError('');
    setAccepting(true);
    try {
      await apiPost(`/api/accept-free-vault/${token}/accept`, { acceptedTermsId: terms.id });
      setAccepted(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation.');
    } finally {
      setAccepting(false);
    }
  }

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Cedar Vault</h1>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Cedar Vault</h1>
          <AlertMessage type="error" message={error || 'This invitation link is not valid.'} />
          <div className="auth-links"><Link to="/login">Go to sign in</Link></div>
        </div>
      </div>
    );
  }

  if (invite.isExpired) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Cedar Vault</h1>
          <p className="auth-subtitle">Invitation Expired</p>
          <div className="divider" />
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.6 }}>
            This Forever Free invitation from <strong>{invite.sentByAdminName}</strong> has expired.
            Please contact <a href="mailto:support@cedarvaultapp.com" style={{ color: 'var(--gold)' }}>support@cedarvaultapp.com</a> to request a new one.
          </p>
          <div className="auth-links"><Link to="/login">Go to sign in</Link></div>
        </div>
      </div>
    );
  }

  if (invite.isAccepted || accepted) {
    return (
      <div className="auth-page">
        <div className="auth-container ff-container">
          <div className="ff-celebration">
            <div className="ff-gift-icon">🎁</div>
            <h1 className="ff-headline">Your vault is ready!</h1>
            <p className="ff-subline">Welcome to your Forever Free Cedar Vault.</p>
          </div>
          <div className="auth-links"><Link to="/">Go to your vault</Link></div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container ff-container">
        <h1 className="auth-title">Cedar Vault</h1>

        <div className="ff-gift-hero">
          <div className="ff-gift-icon">🎁</div>
          <p className="ff-from-label">A personal gift from <strong>{invite.sentByAdminName}</strong></p>
          <h2 className="ff-headline">Lifetime access to Cedar Vault</h2>
          <p className="ff-subline">No subscription. No credit card. No expiration date. Yours to keep, forever.</p>
        </div>

        <div className="divider" />

        <AlertMessage type="error" message={error} />

        {terms && (
          <div className="terms-section">
            <h3 className="terms-heading">Terms & Conditions (v{terms.version})</h3>
            <div
              className="terms-content"
              dangerouslySetInnerHTML={{ __html: marked(terms.content) as string }}
              onScroll={e => {
                const el = e.currentTarget;
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20)
                  setTermsScrolled(true);
              }}
            />
            {!termsScrolled && (
              <p className="terms-scroll-hint">↓ Please scroll through the terms above to continue</p>
            )}
            <label className="terms-checkbox">
              <input
                type="checkbox"
                checked={termsAccepted}
                disabled={!termsScrolled}
                onChange={e => setTermsAccepted(e.target.checked)}
              />
              <span>I agree to the Terms & Conditions (v{terms.version})</span>
            </label>
          </div>
        )}

        {user ? (
          <button
            className="btn-primary invite-accept-btn"
            onClick={handleAccept}
            disabled={accepting || !termsAccepted}
          >
            {accepting ? <LoadingSpinner small /> : 'Accept Your Gift'}
          </button>
        ) : (
          <div className="ff-auth-buttons">
            <button
              className="btn-primary invite-accept-btn"
              onClick={handleCreateAccount}
              disabled={!termsAccepted}
            >
              Create Account to Accept
            </button>
            <button
              className="btn-secondary invite-accept-btn"
              onClick={handleSignIn}
              disabled={!termsAccepted}
            >
              Sign In to Accept
            </button>
          </div>
        )}

        <p className="ff-fine-print">
          This invitation was sent to <strong>{invite.maskedEmail}</strong> and expires in 30 days.
          Questions? <a href="mailto:support@cedarvaultapp.com">support@cedarvaultapp.com</a>
        </p>
      </div>
    </div>
  );
}
